'use client';

import Image from 'next/image';
import { ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import HideIntercomChat from '~/components/core/intercomeChat/HideIntercomChat';
import Logo from '~/components/core/Logo/Logo';
import Flex from '~/components/customComponents/flex/Flex';
import NoAccountYetClient from '../_components/NoAccountYetClient';
import TermsAndConditionsClient from '../_components/TermsAndConditionsClient';
import { LocaleEnum } from '~/locales/resources';
import { ROUTES_MAP } from '~/constants/routes';
import { AndercoreExternalLinks } from '~/constants/endpoints';

type AuthLayoutWrapperProps = {
  children: ReactNode;
  locale: LocaleEnum;
};

/**
 * Auth page layout wrapper (client component)
 * Checks if this is the register page and renders accordingly
 */
const AuthLayoutWrapper = ({ children, locale }: AuthLayoutWrapperProps) => {
  const pathname = usePathname();
  const isRegisterPage = pathname?.includes(ROUTES_MAP.authRegister);
  const isRegisterSuccessPage = pathname?.includes(ROUTES_MAP.authRegisterSuccess);
  const isChoosePage = pathname?.includes(ROUTES_MAP.authChoose);

  // For register, register-success, and choose pages, return just children without the layout
  if (isRegisterPage || isRegisterSuccessPage || isChoosePage) {
    return <>{children}</>;
  }

  // For Login, Forgot Password and Reset Password pages, return the layout with the header and the background
  return (
    <div className="w-full max-w-screen">
      <Flex className="item-center justify-between py-6 px-7 fixed top-0 left-0 w-full z-100">
        <Logo
          logoColor="black"
          href={AndercoreExternalLinks[locale as LocaleEnum]?.andercoreHome}
        />
      </Flex>
      <Flex className="w-screen h-screen">
        <Flex className="min-h-screen items-start w-screen md:w-1/2">
          <Image
            className="w-full h-screen object-cover filter brightness-100 opacity-75"
            src={'/images/auth-background.webp'}
            alt={'background'}
            width={475}
            height={712}
            priority
          />
        </Flex>
        <Flex className="gap-8 md:gap-0 md:min-h-screen bg-white md:w-1/2 md:max-w-1/2 flex-col justify-between fixed md:relative rounded-2xl md:rounded-none py-8 md:py-24 top-1/2 md:top-auto left-1/2 md:left-auto transform md:transform-none -translate-x-1/2 -translate-y-1/2 w-11/12 sm:w-10/12 md:max-w-1/2">
          <Flex className="flex-col justify-center px-6 md:px-24 grow">{children}</Flex>
          {/* No account yet? Register now link added below */}
          <NoAccountYetClient locale={locale} />
          {/* Terms and conditions */}
          <TermsAndConditionsClient locale={locale} />
        </Flex>
        <HideIntercomChat />
      </Flex>
    </div>
  );
};

export default AuthLayoutWrapper;
